/** Days of platform charges shown on Billing → Payment Method. */
export const RECENT_PLATFORM_CHARGES_DAYS = 30;

/** Demo indices (0-based) that seed a non-Paid charge for UI testing. */
export const DEMO_UNPAID_CHARGE_INDEX = 2;
export const DEMO_OPEN_CHARGE_INDEX = 5;

/**
 * Activity timestamp for filtering: paidAt, else attemptedAt, else createdAt.
 */
export function chargeActivityAt(charge) {
  const raw = charge?.paidAt || charge?.attemptedAt || charge?.createdAt;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : NaN;
}

/**
 * Normalize charge status to Open / Paid / Unpaid (same vocabulary as invoices).
 * Maps Stripe-ish SUCCEEDED/FAILED onto Paid/Unpaid.
 */
export function chargePaymentStatus(charge) {
  const key = String(charge?.status || '').toUpperCase();
  if (key === 'PAID' || key === 'SUCCEEDED' || key === 'SUCCESS') return 'PAID';
  if (
    key === 'UNPAID' ||
    key === 'FAILED' ||
    key === 'DECLINED' ||
    key === 'CANCELED' ||
    key === 'CANCELLED'
  ) {
    return 'UNPAID';
  }
  if (key === 'OPEN' || key === 'PENDING' || key === 'PROCESSING' || key === 'REQUIRES_ACTION') {
    return 'OPEN';
  }
  // Legacy rows with a receipt (or paidAt and no failure flag) count as Paid.
  if (charge?.receiptUrl || charge?.paidAt) return 'PAID';
  return 'OPEN';
}

export function isChargePaid(charge) {
  return chargePaymentStatus(charge) === 'PAID';
}

/** True when demo seed includes Open + Unpaid charge examples. */
export function hasChargeStatusDemoSeed(payments = []) {
  const rows = Array.isArray(payments) ? payments : [];
  if (rows.length < 6) return false;
  const statuses = new Set(rows.map((row) => chargePaymentStatus(row)));
  return statuses.has('UNPAID') && statuses.has('OPEN') && statuses.has('PAID');
}

/**
 * Keep charges with activity within the last `days` (default 30), newest first.
 * Includes Open/Unpaid attempts (attemptedAt), not only successful paidAt rows.
 * Optional `limit` caps rows when set to a finite non-negative number.
 */
export function filterRecentPlatformCharges(
  payments = [],
  {
    days = RECENT_PLATFORM_CHARGES_DAYS,
    limit,
    now = Date.now(),
  } = {}
) {
  const cutoff = Number(now) - Number(days) * 24 * 60 * 60 * 1000;
  const rows = (Array.isArray(payments) ? payments : [])
    .filter((pay) => {
      const at = chargeActivityAt(pay);
      return Number.isFinite(at) && at >= cutoff;
    })
    .sort((a, b) => chargeActivityAt(b) - chargeActivityAt(a));

  const max = Number(limit);
  if (!Number.isFinite(max) || max < 0) return rows;
  return rows.slice(0, max);
}
